import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Add, ArrowBack, CheckCircle, GridView, NavigateBeforeRounded, NavigateNextRounded } from '@mui/icons-material'
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { BlueprintFamilyExampleImage, BlueprintFamilyExampleImageInput, isSameExampleImage } from '../../../api/blueprintFamilies/exampleImages'
import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import { GameIcon } from '../../../features/game'
import { TrackedGame } from '../../../features/live-events/types/TrackedGame'
import { borderRadiusDefault } from '../../../themes/theme'
import { useGetTrackedGamesQuery } from '../../api/internal'
import { gameEventTypesSelectMenuAllValue } from '../../features/live-events-tracking/components/TrackedGamesQuickSearchEvent/GameEventTypesSelectMenu/GameEventTypesSelectMenu'
import {
  QuickSearchFilters,
  TrackedGamesQuickSearchEvent,
} from '../../features/live-events-tracking/components/TrackedGamesQuickSearchEvent/TrackedGamesQuickSearchEvent'
import { InternalTrackingEventEvent } from '../../types/InternalTrackingEvent'

type BlueprintFamilyImagePickerProps = {
  open: boolean
  onClose: () => void
  onSelect: (image: BlueprintFamilyExampleImage) => void
  // Entries already on the family, so a shot that is in cannot be added twice.
  selectedImages?: BlueprintFamilyExampleImageInput[]
}

const THUMBNAIL_SIZE = 150
const FILMSTRIP_THUMBNAIL_SIZE = 56
// Leaves room in an `md` dialog for the header, the filmstrip and the action row
// without the whole pane scrolling.
const PREVIEW_MAX_HEIGHT = '52vh'

// An event with nothing to pick from is a dead end for the analyst, so the event
// step hides it. The list carries every event's screenshots, so this needs no
// second request.
const hasScreenshots = (event: InternalTrackingEventEvent): boolean => (event.screenshotUrls?.length ?? 0) > 0

/**
 * Picks one screenshot off one live event, for use as a Blueprint Family example
 * image. Three steps, because the analyst has to narrow from ~1000 tracked games
 * down to a single shot: game, then event, then screenshot.
 *
 * The screenshot step is deliberately explicit rather than defaulting to the
 * event's first shot — an event usually has several, and which one illustrates the
 * blueprint is exactly the judgement call we are asking the analyst to make. So the
 * grid is for browsing only: clicking a thumbnail opens it at full size, with
 * previous/next and a filmstrip to compare neighbours, and the add action lives
 * there. A 150px thumbnail of a phone capture is not enough to tell two "Info"
 * screens apart, and a shot added by mistake is a shot the customer sees.
 *
 * Steps 1 and 2 are existing internal machinery: the tracked-games list and
 * `TrackedGamesQuickSearchEvent`, the same component the TinyMCE live-event link
 * plugin embeds for its own "pick one event" flow. The events list it fetches
 * carries each event's `screenshotUrls`, so step 3 draws from the same response.
 */
export const BlueprintFamilyImagePicker: FC<BlueprintFamilyImagePickerProps> = ({ open, onClose, onSelect, selectedImages = [] }) => {
  const { t } = useTranslation()
  const { data: trackedGames, isLoading: isLoadingGames } = useGetTrackedGamesQuery(undefined, { skip: !open })

  const [selectedGame, setSelectedGame] = useState<TrackedGame | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<InternalTrackingEventEvent | null>(null)
  // The shot open at full size within the screenshot step; null shows the grid.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  // `TrackedGamesQuickSearchEvent` reads its filters off form context rather than
  // props, so it needs a provider even though this dialog has no form of its own.
  const eventFilterForm = useForm<QuickSearchFilters>({
    defaultValues: { eventName: '', eventTypeId: gameEventTypesSelectMenuAllValue },
  })

  const sortedGames = useMemo(() => {
    return [...(trackedGames || [])].sort((a, b) => a.game.resolvedName.localeCompare(b.game.resolvedName))
  }, [trackedGames])

  const resetAndClose = useCallback(() => {
    setSelectedGame(null)
    setSelectedEvent(null)
    setPreviewIndex(null)
    eventFilterForm.reset({ eventName: '', eventTypeId: gameEventTypesSelectMenuAllValue })
    onClose()
  }, [eventFilterForm, onClose])

  const handleGameChange = useCallback((_event: unknown, game: TrackedGame | null) => {
    setSelectedGame(game)
    setSelectedEvent(null)
    setPreviewIndex(null)
  }, [])

  const handleBackToGame = useCallback(() => {
    setSelectedGame(null)
    setSelectedEvent(null)
    setPreviewIndex(null)
  }, [])

  const handleBackToEvent = useCallback(() => {
    setSelectedEvent(null)
    setPreviewIndex(null)
  }, [])

  const handleEventChoose = useCallback((event: InternalTrackingEventEvent) => {
    setSelectedEvent(event)
    setPreviewIndex(null)
  }, [])

  const handleBackToGrid = useCallback(() => setPreviewIndex(null), [])

  const handleScreenshotSelect = useCallback(
    (url: string, screenshotIndex: number) => {
      if (!selectedEvent?.id || !selectedGame) return

      // Only `url` and `eventId` are sent on save. The rest is what the BE will
      // resolve from the event on read, filled in here so the new tile captions
      // itself the same way a saved one does, without a round trip.
      onSelect({
        url,
        eventId: selectedEvent.id,
        eventTypeId: selectedEvent.typeId,
        // The events list leaves `gameId` out of its projection; the game the
        // analyst reached the event through is the same game by construction.
        gameId: selectedEvent.gameId || selectedGame.game.id,
        gameNames: selectedGame.game.names,
        eventName: selectedEvent.name,
        screenshotIndex,
      })
      resetAndClose()
    },
    [onSelect, resetAndClose, selectedEvent, selectedGame]
  )

  const step = selectedEvent ? 'screenshot' : selectedGame ? 'event' : 'game'
  const screenshotUrls = selectedEvent?.screenshotUrls || []
  const screenshotCount = screenshotUrls.length

  const isAlreadyAdded = useCallback(
    (url: string) => Boolean(selectedEvent?.id) && selectedImages.some((image) => isSameExampleImage(image, { url, eventId: selectedEvent?.id as string })),
    [selectedEvent?.id, selectedImages]
  )

  const showPrevious = useCallback(() => {
    setPreviewIndex((index) => (index === null ? null : (index - 1 + screenshotCount) % screenshotCount))
  }, [screenshotCount])

  const showNext = useCallback(() => {
    setPreviewIndex((index) => (index === null ? null : (index + 1) % screenshotCount))
  }, [screenshotCount])

  // Arrow keys page through the shots while one is enlarged, so comparing
  // neighbours does not mean aiming at the small navigation buttons each time.
  useEffect(() => {
    if (previewIndex === null || screenshotCount < 2) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        showPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        showNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previewIndex, screenshotCount, showPrevious, showNext])

  const previewUrl = previewIndex !== null ? screenshotUrls[previewIndex] : undefined
  const previewAlreadyAdded = previewUrl !== undefined && isAlreadyAdded(previewUrl)

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <GRDialogTitle onClose={resetAndClose}>{t('internal-live-events:blueprint_image_picker_title')}</GRDialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {step === 'game' && (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('internal-live-events:blueprint_image_picker_step_game')}
            </Typography>
            <Autocomplete
              value={selectedGame}
              onChange={handleGameChange}
              options={sortedGames}
              loading={isLoadingGames}
              getOptionLabel={(game) => game.game.resolvedName}
              isOptionEqualToValue={(option, value) => option.game.id === value.game.id}
              fullWidth
              size="small"
              autoHighlight
              openOnFocus
              renderInput={(params) => <TextField {...params} autoFocus placeholder={t('internal-live-events:blueprint_image_picker_game_placeholder')} />}
              renderOption={(props, game) => (
                <li {...props} key={game.game.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <GameIcon src={game.game.getIcon() as string} gameName={game.game.resolvedName} size="small" gamePlatforms={game.game.platforms} />
                    <Stack sx={{ minWidth: 0 }}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {game.game.resolvedName}
                      </Box>
                      {game.game.artist && (
                        <Typography variant="caption" color="text.secondary">
                          {game.game.artist}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </li>
              )}
            />
          </Box>
        )}

        {step === 'event' && selectedGame && (
          <>
            <Stack direction="row" alignItems="center" spacing={1} px={2} pt={2}>
              <Button size="small" startIcon={<ArrowBack />} onClick={handleBackToGame}>
                {t('internal-live-events:blueprint_image_picker_back')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('internal-live-events:blueprint_image_picker_step_event', { game: selectedGame.game.resolvedName })}
              </Typography>
            </Stack>
            <FormProvider {...eventFilterForm}>
              <TrackedGamesQuickSearchEvent
                trackedGameId={selectedGame.game.id}
                filterEvents={hasScreenshots}
                renderActions={(event) => (
                  <Button size="small" variant="contained" disabled={!event.id} onClick={() => handleEventChoose(event)}>
                    {t('internal-live-events:blueprint_image_picker_select_event')}
                  </Button>
                )}
              />
            </FormProvider>
          </>
        )}

        {step === 'screenshot' && selectedEvent && previewIndex === null && (
          <Box p={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Button size="small" startIcon={<ArrowBack />} onClick={handleBackToEvent}>
                {t('internal-live-events:blueprint_image_picker_back')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('internal-live-events:blueprint_image_picker_step_screenshot', { event: selectedEvent.name })}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5, ml: 1 }}>
              {t('internal-live-events:blueprint_image_picker_screenshot_hint')}
            </Typography>

            {screenshotCount === 0 ? (
              // Unreachable through the event step, which hides events without
              // shots, but kept so a stale list cannot leave the analyst on a
              // blank pane.
              <Alert severity="info">{t('internal-live-events:blueprint_image_picker_no_screenshots')}</Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${THUMBNAIL_SIZE}px, 1fr))`,
                  gap: 2,
                }}
              >
                {screenshotUrls.map((url, index) => {
                  const added = isAlreadyAdded(url)
                  return (
                    <Box
                      key={`${url}-${index}`}
                      component="button"
                      type="button"
                      onClick={() => setPreviewIndex(index)}
                      aria-label={t('internal-live-events:blueprint_image_picker_screenshot_alt', { index: index + 1 })}
                      sx={{
                        position: 'relative',
                        p: 0,
                        aspectRatio: '1 / 1',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'grey.100',
                        cursor: 'zoom-in',
                        '&:hover, &:focus-visible': {
                          borderColor: 'primary.main',
                          boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}`,
                          outline: 'none',
                        },
                      }}
                    >
                      {/* Uncropped, so a landscape capture is not reduced to its middle
                          third: at this size the whole composition is what can be told
                          apart, not the detail. */}
                      <Box
                        component="img"
                        src={url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: added ? 0.55 : 1 }}
                      />
                      {added && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            left: 4,
                            right: 4,
                            justifyContent: 'center',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: borderRadiusDefault,
                            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                          }}
                        >
                          <CheckCircle color="success" sx={{ fontSize: 16 }} />
                          <Typography variant="caption">{t('internal-live-events:blueprint_image_picker_already_added')}</Typography>
                        </Stack>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        )}

        {step === 'screenshot' && selectedEvent && previewIndex !== null && previewUrl && (
          <Box p={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Button size="small" startIcon={<GridView />} onClick={handleBackToGrid}>
                {t('internal-live-events:blueprint_image_picker_all_screenshots')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('internal-live-events:blueprint_image_picker_screenshot_counter', {
                  index: previewIndex + 1,
                  total: screenshotCount,
                  event: selectedEvent.name,
                })}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                onClick={showPrevious}
                disabled={screenshotCount < 2}
                aria-label={t('internal-live-events:blueprint_image_picker_previous_screenshot')}
              >
                <NavigateBeforeRounded />
              </IconButton>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: PREVIEW_MAX_HEIGHT,
                  borderRadius: 1,
                  backgroundColor: 'grey.100',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={previewUrl}
                  alt={t('internal-live-events:blueprint_image_picker_screenshot_alt', { index: previewIndex + 1 })}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
              </Box>
              <IconButton onClick={showNext} disabled={screenshotCount < 2} aria-label={t('internal-live-events:blueprint_image_picker_next_screenshot')}>
                <NavigateNextRounded />
              </IconButton>
            </Stack>

            {/* Filmstrip: the same shots as the grid, small, so the analyst can jump
                straight to a neighbour instead of paging one at a time. */}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, px: 6, overflowX: 'auto', pb: 0.5 }}>
              {screenshotUrls.map((url, index) => {
                const isCurrent = index === previewIndex
                return (
                  <Box
                    key={`${url}-${index}`}
                    component="button"
                    type="button"
                    onClick={() => setPreviewIndex(index)}
                    aria-label={t('internal-live-events:blueprint_image_picker_screenshot_alt', { index: index + 1 })}
                    aria-current={isCurrent ? 'true' : undefined}
                    sx={{
                      flexShrink: 0,
                      width: FILMSTRIP_THUMBNAIL_SIZE,
                      height: FILMSTRIP_THUMBNAIL_SIZE,
                      p: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundColor: 'grey.100',
                      border: (theme) => `2px solid ${isCurrent ? theme.palette.primary.main : theme.palette.divider}`,
                      opacity: isCurrent ? 1 : 0.7,
                      '&:hover, &:focus-visible': { opacity: 1, outline: 'none', borderColor: 'primary.main' },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                )
              })}
            </Stack>
          </Box>
        )}
      </DialogContent>

      {/* Only the enlarged view has an action to take, so the footer only exists
          while it is showing — everything else is pure navigation, handled inline. */}
      {step === 'screenshot' && previewIndex !== null && previewUrl && (
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          {previewAlreadyAdded && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main', mr: 'auto' }}>
              <CheckCircle fontSize="small" />
              <Typography variant="body2">{t('internal-live-events:blueprint_image_picker_already_added')}</Typography>
            </Stack>
          )}
          <Button variant="contained" startIcon={<Add />} disabled={previewAlreadyAdded} onClick={() => handleScreenshotSelect(previewUrl, previewIndex)}>
            {t('internal-live-events:blueprint_image_picker_add_screenshot')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
